<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import {
  CUSTOM_SLOT_DEFAULT_INTERVAL_MINUTES,
  CUSTOM_SLOT_MAX_DURATION_MINUTES,
  CUSTOM_SLOT_MAX_INTERVAL_MINUTES,
  CUSTOM_SLOT_MIN_DURATION_MINUTES,
  CUSTOM_SLOT_MIN_INTERVAL_MINUTES,
  CUSTOM_SLOT_STRATEGIES,
  createCustomSlotId,
  normalizeCustomSlots,
  normalizeHm,
} from 'shared/helpers/bookingSlotOptions';

const emit = defineEmits(['create']);
const { t } = useI18n();

const dialogRef = ref(null);

const formState = reactive({
  label: '',
  durationMinutes: '180',
  slotIntervalMinutes: String(CUSTOM_SLOT_DEFAULT_INTERVAL_MINUTES),
  strategy: '',
  startTime: '17:00',
  endTime: '22:00',
  weekdays: [],
});

const resetForm = () => {
  formState.label = '';
  formState.durationMinutes = '180';
  formState.slotIntervalMinutes = String(CUSTOM_SLOT_DEFAULT_INTERVAL_MINUTES);
  formState.strategy = '';
  formState.startTime = '17:00';
  formState.endTime = '22:00';
  formState.weekdays = [];
};

const validDuration = value => {
  const n = Number(value);
  return (
    Number.isFinite(n) &&
    n >= CUSTOM_SLOT_MIN_DURATION_MINUTES &&
    n <= CUSTOM_SLOT_MAX_DURATION_MINUTES &&
    n % 5 === 0
  );
};

const validInterval = value => {
  const n = Number(value);
  return (
    Number.isFinite(n) &&
    n >= CUSTOM_SLOT_MIN_INTERVAL_MINUTES &&
    n <= CUSTOM_SLOT_MAX_INTERVAL_MINUTES &&
    n % 5 === 0
  );
};

const validTime = value => !!normalizeHm(value);

const endAfterStart = () => {
  const start = normalizeHm(formState.startTime);
  const end = normalizeHm(formState.endTime);
  if (!start || !end) return false;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  return eh * 60 + em > sh * 60 + sm;
};

const rules = {
  durationMinutes: {
    required: helpers.withMessage(
      () => t('BOOKING_WIDGETS.SLOT_OPTIONS.CUSTOM_DIALOG.DURATION_ERROR'),
      required
    ),
    validDuration: helpers.withMessage(
      () => t('BOOKING_WIDGETS.SLOT_OPTIONS.CUSTOM_DIALOG.DURATION_ERROR'),
      validDuration
    ),
  },
  slotIntervalMinutes: {
    required: helpers.withMessage(
      () => t('BOOKING_WIDGETS.SLOT_OPTIONS.CUSTOM_DIALOG.INTERVAL_ERROR'),
      required
    ),
    validInterval: helpers.withMessage(
      () => t('BOOKING_WIDGETS.SLOT_OPTIONS.CUSTOM_DIALOG.INTERVAL_ERROR'),
      validInterval
    ),
  },
  startTime: {
    required: helpers.withMessage(
      () => t('BOOKING_WIDGETS.SLOT_OPTIONS.CUSTOM_DIALOG.TIME_ERROR'),
      required
    ),
    validTime: helpers.withMessage(
      () => t('BOOKING_WIDGETS.SLOT_OPTIONS.CUSTOM_DIALOG.TIME_ERROR'),
      validTime
    ),
  },
  endTime: {
    required: helpers.withMessage(
      () => t('BOOKING_WIDGETS.SLOT_OPTIONS.CUSTOM_DIALOG.TIME_ERROR'),
      required
    ),
    validTime: helpers.withMessage(
      () => t('BOOKING_WIDGETS.SLOT_OPTIONS.CUSTOM_DIALOG.TIME_ERROR'),
      validTime
    ),
    endAfterStart: helpers.withMessage(
      () => t('BOOKING_WIDGETS.SLOT_OPTIONS.CUSTOM_DIALOG.TIME_RANGE_ERROR'),
      endAfterStart
    ),
  },
};

const v$ = useVuelidate(rules, formState);

const strategyOptions = computed(() => [
  {
    id: '',
    label: t('BOOKING_WIDGETS.SLOT_OPTIONS.CUSTOM_DIALOG.STRATEGY_DEFAULT'),
  },
  ...CUSTOM_SLOT_STRATEGIES.map(id => ({
    id,
    label: t(
      `BOOKING_WIDGETS.SLOT_OPTIONS.CUSTOM_DIALOG.STRATEGY_${id.toUpperCase()}`
    ),
  })),
]);

const weekdayOptions = computed(() =>
  [0, 1, 2, 3, 4, 5, 6].map(day => ({
    id: day,
    label: t(`BOOKING_WIDGETS.SLOT_OPTIONS.CUSTOM_DIALOG.WEEKDAY.${day}`),
  }))
);

const isWeekdayChecked = day => formState.weekdays.includes(day);

const toggleWeekday = day => {
  if (formState.weekdays.includes(day)) {
    formState.weekdays = formState.weekdays.filter(d => d !== day);
  } else {
    formState.weekdays = [...formState.weekdays, day].sort((a, b) => a - b);
  }
};

const handleDialogConfirm = async () => {
  const isFormCorrect = await v$.value.$validate();
  if (!isFormCorrect) return;

  const [normalized] = normalizeCustomSlots([
    {
      id: createCustomSlotId(),
      label: formState.label,
      durationMinutes: Number(formState.durationMinutes),
      slotIntervalMinutes: Number(formState.slotIntervalMinutes),
      strategy: formState.strategy || null,
      startTime: formState.startTime,
      endTime: formState.endTime,
      weekdays: formState.weekdays,
    },
  ]);
  if (!normalized) return;

  emit('create', normalized);
  resetForm();
  v$.value.$reset();
};

defineExpose({ dialogRef, resetForm });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('BOOKING_WIDGETS.SLOT_OPTIONS.CUSTOM_DIALOG.HEADER')"
    :confirm-button-label="t('BOOKING_WIDGETS.SLOT_OPTIONS.CUSTOM_DIALOG.CONFIRM')"
    @confirm="handleDialogConfirm"
  >
    <div class="space-y-4">
      <Input
        v-model="formState.label"
        :label="t('BOOKING_WIDGETS.SLOT_OPTIONS.CUSTOM_DIALOG.LABEL')"
        :placeholder="
          t('BOOKING_WIDGETS.SLOT_OPTIONS.CUSTOM_DIALOG.LABEL_PLACEHOLDER')
        "
      />
      <p class="-mt-2 text-xs text-n-slate-11">
        {{ t('BOOKING_WIDGETS.SLOT_OPTIONS.CUSTOM_DIALOG.LABEL_HELP') }}
      </p>

      <div class="grid grid-cols-2 gap-3">
        <Input
          v-model="formState.durationMinutes"
          type="number"
          :min="CUSTOM_SLOT_MIN_DURATION_MINUTES"
          :max="CUSTOM_SLOT_MAX_DURATION_MINUTES"
          step="5"
          :label="t('BOOKING_WIDGETS.SLOT_OPTIONS.CUSTOM_DIALOG.DURATION')"
          :placeholder="
            t('BOOKING_WIDGETS.SLOT_OPTIONS.CUSTOM_DIALOG.DURATION_PLACEHOLDER')
          "
          :message="
            v$.durationMinutes.$error
              ? v$.durationMinutes.$errors[0].$message
              : ''
          "
          :message-type="v$.durationMinutes.$error ? 'error' : 'info'"
          @blur="v$.durationMinutes.$touch()"
        />
        <Input
          v-model="formState.slotIntervalMinutes"
          type="number"
          :min="CUSTOM_SLOT_MIN_INTERVAL_MINUTES"
          :max="CUSTOM_SLOT_MAX_INTERVAL_MINUTES"
          step="5"
          :label="t('BOOKING_WIDGETS.SLOT_OPTIONS.CUSTOM_DIALOG.INTERVAL')"
          :placeholder="
            t('BOOKING_WIDGETS.SLOT_OPTIONS.CUSTOM_DIALOG.INTERVAL_PLACEHOLDER')
          "
          :message="
            v$.slotIntervalMinutes.$error
              ? v$.slotIntervalMinutes.$errors[0].$message
              : ''
          "
          :message-type="v$.slotIntervalMinutes.$error ? 'error' : 'info'"
          @blur="v$.slotIntervalMinutes.$touch()"
        />
      </div>
      <p class="-mt-2 text-xs text-n-slate-11">
        {{ t('BOOKING_WIDGETS.SLOT_OPTIONS.CUSTOM_DIALOG.INTERVAL_HELP') }}
      </p>

      <label class="block text-sm font-medium text-n-slate-12">
        {{ t('BOOKING_WIDGETS.SLOT_OPTIONS.CUSTOM_DIALOG.STRATEGY') }}
        <select
          v-model="formState.strategy"
          class="mt-1.5 w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm"
        >
          <option
            v-for="opt in strategyOptions"
            :key="opt.id || 'default'"
            :value="opt.id"
          >
            {{ opt.label }}
          </option>
        </select>
      </label>
      <p class="-mt-2 text-xs text-n-slate-11">
        {{ t('BOOKING_WIDGETS.SLOT_OPTIONS.CUSTOM_DIALOG.STRATEGY_HELP') }}
      </p>

      <div class="grid grid-cols-2 gap-3">
        <Input
          v-model="formState.startTime"
          type="time"
          :label="t('BOOKING_WIDGETS.SLOT_OPTIONS.CUSTOM_DIALOG.START_TIME')"
          :message="
            v$.startTime.$error ? v$.startTime.$errors[0].$message : ''
          "
          :message-type="v$.startTime.$error ? 'error' : 'info'"
          @blur="v$.startTime.$touch()"
        />
        <Input
          v-model="formState.endTime"
          type="time"
          :label="t('BOOKING_WIDGETS.SLOT_OPTIONS.CUSTOM_DIALOG.END_TIME')"
          :message="v$.endTime.$error ? v$.endTime.$errors[0].$message : ''"
          :message-type="v$.endTime.$error ? 'error' : 'info'"
          @blur="v$.endTime.$touch()"
        />
      </div>
      <p class="-mt-2 text-xs text-n-slate-11">
        {{ t('BOOKING_WIDGETS.SLOT_OPTIONS.CUSTOM_DIALOG.TIME_HELP') }}
      </p>

      <div>
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('BOOKING_WIDGETS.SLOT_OPTIONS.CUSTOM_DIALOG.WEEKDAYS') }}
        </span>
        <p class="mt-1 text-xs text-n-slate-11">
          {{ t('BOOKING_WIDGETS.SLOT_OPTIONS.CUSTOM_DIALOG.WEEKDAYS_HELP') }}
        </p>
        <div class="mt-2 flex flex-wrap gap-x-3 gap-y-1.5">
          <label
            v-for="day in weekdayOptions"
            :key="day.id"
            class="flex cursor-pointer select-none items-center gap-1.5 text-xs text-n-slate-12"
          >
            <input
              type="checkbox"
              class="rounded border-n-weak"
              :checked="isWeekdayChecked(day.id)"
              @change="toggleWeekday(day.id)"
            />
            {{ day.label }}
          </label>
        </div>
      </div>
    </div>
  </Dialog>
</template>
